import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { AxisMove, PuzzleDimensions, PuzzleState } from "./types";
import { applyMove, createInitialPuzzle, isSolved, moveAxes } from "./axisPuzzleEngine";
import {
  createInitialClassicPuzzle,
  isClassicSolved,
  swapWithEmpty,
  ClassicPuzzleState
} from "./classicPuzzleEngine";

interface HistoryEntry {
  grid: PuzzleState["grid"];
}

export type Difficulty = "easy" | "medium" | "hard";
export type PuzzleMode = "axis" | "classic";

const DIFFICULTY_DIMENSIONS: Record<Difficulty, PuzzleDimensions> = {
  easy: { rows: 3, cols: 3 },
  medium: { rows: 4, cols: 4 },
  hard: { rows: 6, cols: 6 }
};

export interface PuzzleStore {
  // Axis mode state
  state: PuzzleState;
  // Classic mode state
  classicState: ClassicPuzzleState | null;
  history: HistoryEntry[];
  startedAt: number | null;
  completedAt: number | null;
  isShowingSolution: boolean;
  difficulty: Difficulty;
  mode: PuzzleMode;
  setMode(mode: PuzzleMode): void;
  setDifficulty(difficulty: Difficulty): void;
  setShowSolution(show: boolean): void;
  reset(seed?: number): void;
  nudgeAxis(delta: { dVertical?: number; dHorizontal?: number }): void;
  performMove(move: AxisMove): void;
  swapClassicPiece(tilePos: { row: number; col: number }): void;
  undo(): void;
}

const DEFAULT_DIFFICULTY: Difficulty = "medium";
const DEFAULT_MODE: PuzzleMode = "axis";

export const usePuzzleStore = create<PuzzleStore>()(
  devtools((set, get) => ({
    state: createInitialPuzzle(DIFFICULTY_DIMENSIONS[DEFAULT_DIFFICULTY]),
    classicState: null,
    history: [],
    startedAt: null,
    completedAt: null,
    isShowingSolution: false,
    difficulty: DEFAULT_DIFFICULTY,
    mode: DEFAULT_MODE,

    setMode(mode) {
      const dimensions = DIFFICULTY_DIMENSIONS[get().difficulty];
      if (mode === "classic") {
        const classicState = createInitialClassicPuzzle(dimensions, Date.now());
        set({
          mode,
          classicState,
          state: get().state, // Keep axis state for switching back
          history: [],
          startedAt: Date.now(),
          completedAt: null,
          isShowingSolution: false
        });
      } else {
        const state = createInitialPuzzle(dimensions, Date.now());
        set({
          mode,
          state,
          classicState: get().classicState, // Keep classic state for switching back
          history: [],
          startedAt: Date.now(),
          completedAt: null,
          isShowingSolution: false
        });
      }
    },

    setDifficulty(difficulty) {
      const dimensions = DIFFICULTY_DIMENSIONS[difficulty];
      const mode = get().mode;
      if (mode === "classic") {
        const classicState = createInitialClassicPuzzle(dimensions, Date.now());
        set({
          difficulty,
          classicState,
          history: [],
          startedAt: Date.now(),
          completedAt: null,
          isShowingSolution: false
        });
      } else {
        const next = createInitialPuzzle(dimensions, Date.now());
        set({
          difficulty,
          state: next,
          history: [],
          startedAt: Date.now(),
          completedAt: null,
          isShowingSolution: false
        });
      }
    },

    setShowSolution(show) {
      set({ isShowingSolution: show });
    },

    reset(seed) {
      const dimensions = DIFFICULTY_DIMENSIONS[get().difficulty];
      const mode = get().mode;
      if (mode === "classic") {
        const classicState = createInitialClassicPuzzle(dimensions, seed ?? Date.now());
        const solved = isClassicSolved(classicState);
        set({
          classicState,
          history: [],
          startedAt: Date.now(),
          completedAt: solved ? Date.now() : null
        });
      } else {
        const next = createInitialPuzzle(dimensions, seed ?? Date.now());
        set({
          state: next,
          history: [],
          startedAt: Date.now(),
          completedAt: null
        });
      }
    },

    nudgeAxis(delta) {
      const current = get().state;
      const next = moveAxes(current, delta);
      set({ state: next });
    },

    performMove(move) {
      const current = get().state;
      const history = get().history;
      const next = applyMove(current, move);
      const now = Date.now();
      const solved = isSolved(next);
      set({
        state: next,
        history: [...history, { grid: current.grid }],
        startedAt: get().startedAt ?? now,
        completedAt: solved ? now : get().completedAt
      });
    },

    swapClassicPiece(tilePos) {
      const current = get().classicState;
      if (!current) return;
      const next = swapWithEmpty(current, tilePos);
      if (!next) return; // Invalid move (not adjacent to empty)
      const history = get().history;
      const now = Date.now();
      const solved = isClassicSolved(next);
      set({
        classicState: next,
        history: [...history, { grid: current.grid }],
        startedAt: get().startedAt ?? now,
        completedAt: solved ? now : get().completedAt
      });
    },

    undo() {
      const { history, state, classicState, mode } = get();
      if (history.length === 0) return;
      const last = history[history.length - 1];
      if (mode === "classic" && classicState) {
        set({
          classicState: { ...classicState, grid: last.grid },
          history: history.slice(0, -1),
          completedAt: null
        });
      } else {
        set({
          state: { ...state, grid: last.grid },
          history: history.slice(0, -1),
          completedAt: null
        });
      }
    }
  }))
);


